import { useEffect, useState } from 'react';
import type { MultiComboboxOption } from '@/components/ui/MultiCombobox';
import { ApiError, api } from '@/lib/api';
import type { MasterRef, ReferenceRecord } from '@/lib/types';

/**
 * The two account masters that are edited in place — faculty and regions — and
 * the ONE rule each of them has to obey on write.
 *
 * ⚠️ **These are not display helpers. Every function here encodes a write rule
 * the server enforces**, and getting one wrong is a silent data loss or a 422
 * naming a control the user never touched. They live in one module for the
 * reason CLAUDE.md gives for `src/lib/inclusions.ts`: two copies of a rule agree
 * only until one of them is edited.
 *
 * **Both callers now import from here, and there is no second copy.**
 * `CompanyDetailPage.tsx` is where these were written and held private copies
 * of all six until 2026-08-27; they were deleted and pointed at this module,
 * verified identical line by line first. `CompaniesPage.tsx` reads them for the
 * same two inline editors in the list.
 *
 * So a change below reaches both surfaces at once, which is the point — **and
 * the reason not to fork one back out.** The two renderings genuinely differ and
 * that is already handled: `facultyOptions` returns the FACT (`retired`) rather
 * than one spelling of it, because the list folds it into a `<select>` label and
 * the detail page puts it in a `Combobox` hint. If a caller needs different
 * WORDING, map the result at the call site — `facultyPickerOptions` in
 * `CompanyDetailPage.tsx` is that, and it is nine lines. If a caller needs a
 * different RULE, it is not the same rule, and it does not belong in a copy of
 * this file under another name.
 */

/**
 * How a master row is displayed anywhere a company reads one.
 *
 * A name is `NOT NULL` but user-entered, and a blank one would render an
 * invisible `<option>` and an empty chip. The id is at least selectable and
 * identifies the row to whoever has to fix it.
 */
export function masterLabel(id: number, name: string): string {
  const trimmed = name.trim();
  return trimmed === '' ? `#${id}` : trimmed;
}

/**
 * Reads the faculty master.
 *
 * 🔴 CORRECTED 2026-09-18 — EVERY CLAUSE OF THIS PARAGRAPH WAS FALSE, AND THE
 * MIDDLE ONE WAS COSTING A WRONG LABEL. *SUPERSEDED: "Its own route rather than
 * a slot in the parameterised reference module, so there is no shared hook to
 * use — and no `isActive` filter on the wire either: `GET /faculties` takes no
 * params and answers with every row, active or not."*
 *
 * Measured: `faculties` IS a slot in the backend's reference registry, the route
 * DOES take `page`, `perPage`, `isActive` and `search`, and its schema is
 * `perPage: …max(200).default(50)` — so "answers with every row" was **50 rows**.
 *
 * ⛔ THE SYMPTOM WAS A CONFIDENT WRONG STATEMENT RATHER THAN A SHORT LIST, WHICH
 * IS WHY IT SURVIVED. Past 50 faculties, `facultyOptions` below injects the
 * record's stored value — absent from the truncated response — and labels it
 * **"No longer offered"**. A live faculty, on a record somebody is editing,
 * accused of being retired. A blank picker gets reported; this does not.
 *
 * ✅ `api.listFaculties` now asks for `perPage: 200`, the endpoint's ceiling, the
 * same thing `use-reference-options.ts` asks for and for the same reason. ⚠️ It
 * is a CEILING, not a large number: past 200 the list is short again and the
 * mislabel returns.
 *
 * ⚠️ THE `isActive` HALF IS STILL CORRECT AND IS DELIBERATE — no filter is sent,
 * because `facultyOptions` needs the retired rows to tell a genuinely retired
 * value from one the response simply did not carry. The split really is made
 * here; only the reason given for it was wrong.
 *
 * `enabled` exists so a page that cannot write skips the request entirely — it
 * renders the faculty as text and has nothing to fill a picker with.
 * ⚠️ *SUPERSEDED: "the rate limit is 120/60s per IP shared across the whole
 * office."* It is **12,000/60s** (corrected 2026-09-17). Skipping the request is
 * still right — it is a request nothing on the page can use — but the arithmetic
 * that reason cited is off by a factor of a hundred.
 */
export function useFaculties(enabled: boolean) {
  const [records, setRecords] = useState<ReferenceRecord[]>([]);
  // Seeded from `enabled` so the first paint shows a loading picker rather than
  // an empty one, and nothing calls setState synchronously in an effect.
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    api
      .listFaculties(controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setRecords(response.data);
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        // Kept empty, but SAID so: "no faculties" and "the list didn't arrive"
        // look identical in a picker and only one of them is worth retrying.
        setRecords([]);
        setFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [enabled]);

  return { records, loading: enabled ? loading : false, failed };
}

/**
 * One option a single-valued master picker offers, plus whether the value is
 * still on offer.
 *
 * `retired` is returned rather than folded into `label` so a `<select>` can
 * spell it inline and a `Combobox` can put it in a `hint` — the two renderings
 * differ, the FACT does not.
 */
export interface MasterOption {
  value: string;
  label: string;
  /** Stored but no longer active. Only ever true for the value already held. */
  retired: boolean;
}

/**
 * The choices one faculty picker offers.
 *
 * ⚠️ **Faculty is stricter than stage on write**: `facultyId` is checked for
 * `isActive`, so offering a retired row is offering a guaranteed 422 — where the
 * stage editor deliberately keeps retired rows selectable, because it re-sends
 * whatever the row already holds. So only ACTIVE rows are choices here.
 *
 * The stored value is nonetheless injected when it is not among them, because a
 * company whose faculty was retired must not read as unset — that is a
 * different, real state. Re-picking it cannot cause the 422 it warns about: a
 * caller must not write an unchanged value, and a `<select>` fires no change
 * event for the option already selected.
 *
 * Its label comes from the MASTER where the master still carries the row, so a
 * retired-and-renamed faculty reads by its current name rather than by the stale
 * copy sitting on the company record.
 */
export function facultyOptions(records: ReferenceRecord[], stored: MasterRef | null): MasterOption[] {
  const options: MasterOption[] = records
    .filter((record) => record.isActive)
    .map((record) => ({
      value: String(record.id),
      label: masterLabel(record.id, record.name),
      retired: false,
    }));

  if (!stored) return options;
  if (options.some((option) => option.value === String(stored.id))) return options;

  const fresh = records.find((record) => record.id === stored.id);
  return [
    {
      value: String(stored.id),
      label: masterLabel(stored.id, fresh ? fresh.name : stored.name),
      /*
       * An empty `records` proves nothing — the list is still loading, or it
       * failed — so the value is injected WITHOUT the marker. Accusing a live
       * faculty of being retired for the frames before the rows land is worse
       * than saying nothing.
       */
      retired: records.length > 0,
    },
    ...options,
  ];
}

/**
 * The choices the regions multi-select offers: every ACTIVE region, plus any the
 * company already holds that are not among them.
 *
 * ⚠️ **Injecting the held-but-retired ones is not a nicety — it is what makes
 * silent data loss unreachable.** `regionIds` is a whole-set REPLACE, so the
 * array the editor assembles is the array the company ends up with. An options
 * list of active rows only would leave a stored retired region with no chip to
 * represent it, and the next save of an unrelated change would drop it — a
 * **200**, with nothing on screen having said so and nothing to restore from.
 *
 * With the chip present, the id stays in the draft unless a person removes it,
 * so the only two outcomes are "it was sent and the server refused, naming it"
 * and "somebody deliberately took it out". `retiredRegionNames` is what warns,
 * before a save is attempted, that the first of those is what will happen.
 */
export function regionOptions(
  records: ReferenceRecord[],
  stored: readonly MasterRef[],
): MultiComboboxOption[] {
  const active: MultiComboboxOption[] = records.map((record) => ({
    value: String(record.id),
    label: masterLabel(record.id, record.name),
  }));
  const known = new Set(active.map((option) => option.value));

  const held: MultiComboboxOption[] = [];
  for (const region of stored) {
    const value = String(region.id);
    if (known.has(value)) continue;
    known.add(value);
    held.push({
      value,
      label: masterLabel(region.id, region.name),
      // Same rule as the faculty picker: an empty master proves nothing yet.
      ...(records.length > 0 ? { hint: 'Retired — remove it to save this field' } : {}),
    });
  }

  return [...held, ...active];
}

/**
 * The stored regions that are no longer active, or `[]` while that is unknown.
 *
 * Worth surfacing without waiting for a failed save: `regionIds` is written as
 * the WHOLE set, so one retired member refuses every future edit of this field —
 * including one that has nothing to do with it. Saying so up front turns a
 * confusing 422 into a one-click fix.
 *
 * ⚠️ `useReferenceOptions` pins `isActive: true`, so its rows are the active
 * ones by construction and every stored id missing from them is retired. Do not
 * pass an unfiltered master here — every row would look active and this would
 * return `[]` for an account that cannot be saved.
 */
export function retiredRegionNames(
  records: ReferenceRecord[],
  stored: readonly MasterRef[],
): string[] {
  if (records.length === 0) return [];
  const active = new Set(records.map((record) => record.id));
  return stored
    .filter((region) => !active.has(region.id))
    .map((region) => masterLabel(region.id, region.name));
}

/**
 * Which of the ids we just sent the server named in its refusal.
 *
 * The 422 lists EVERY offender in one message — "Not an active region: 998,
 * 999" — precisely so all the bad chips can be marked at once. Intersecting with
 * what was sent keeps a stray number in the prose from marking a chip that was
 * never in question.
 */
export function refusedRegionIds(caught: ApiError, sent: readonly string[]): string[] {
  const text = (caught.details ?? [])
    .filter((detail) => detail.path === '' || detail.path.startsWith('regionIds'))
    .map((detail) => detail.message)
    .join(' ');
  const named = new Set(text.match(/\d+/g) ?? []);
  return sent.filter((value) => named.has(value));
}
